import { useQueries } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import type { Project, Rendition, Segment, Space } from "@scenix/core";
import { api } from "../lib/api";
import { frameStyle, stageStyle } from "../lib/layout";
import { pictureOf, stillAt, timecode } from "../lib/scrub";
import { LayoutStage } from "./LayoutStage";

/** Every asset a layout draws on, at any depth. */
function assetsIn(layout: Segment["brief"]["layout"]): string[] {
  if (!layout) return [];
  if (layout.content) {
    return layout.content.footage ? [layout.content.footage.assetId] : [];
  }
  return (layout.boxes ?? []).flatMap(assetsIn);
}

interface ChainPlayerProps {
  spaceId: Space["id"];
  /** Named because a segment's stills are served from the project (ADR 000070). */
  projectId: Project["id"];
  segments: readonly Segment[];
  renditions: readonly Rendition[];
  mediaUrls: Record<string, string>;
  posterUrls: Record<string, string>;
  durationSec: number;
  /** Where the chain is, in chain time. Shared with the scrub bar below. */
  playheadSec: number;
  onPlayhead: (seconds: number) => void;
}

/**
 * The chain played as one thing, assembled as it goes (issue #200).
 *
 * Not the assembly. The assembly is a file somebody asked for, made by
 * concatenating finished pictures, and it refuses a chain with a hole in it
 * (ADR 000061). This plays the chain as it stands: each segment's own picture
 * in turn, and for a segment with no picture, its stills cut between the way
 * the animatic cuts between them, filling exactly as long as the segment lasts.
 *
 * That difference is the point. A chain is routinely part made, and the
 * question somebody has while making it is whether the thing works at length,
 * which is a question you can answer before every shot exists and long before
 * paying to render the whole cut.
 */
export function ChainPlayer({
  spaceId,
  projectId,
  segments,
  renditions,
  mediaUrls,
  posterUrls,
  durationSec,
  playheadSec,
  onPlayhead,
}: ChainPlayerProps) {
  const video = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);

  // Which segment the playhead is in, and what it has to show. Both derived,
  // so a re-render after the worker finishes something picks the new picture up
  // without this holding a stale copy.
  // Clamped before anything is looked up. A reading from outside the chain used
  // to fall through to the last segment, which turns one bad number into the
  // player jumping to the end.
  const at = Math.min(Math.max(playheadSec, 0), durationSec);
  const active =
    segments.find((s) => at >= s.startSec && at < s.endSec) ?? segments[segments.length - 1];
  const picture = active ? pictureOf(renditions, active) : undefined;
  const madePicture = picture && active?.state === "ready" ? mediaUrls[picture.id] : undefined;
  const poster = picture ? posterUrls[picture.id] : undefined;

  /*
   * The footage a segment is cut from, before anybody has cut it.
   *
   * A brief that names a source names an unambiguous piece of film: this asset,
   * from here to there. Waiting for a render to watch it is waiting for an
   * encode to tell you something the source already says, and a chain that has
   * been planned but not rendered is the ordinary state of a project being
   * made.
   *
   * Every distinct asset the chain draws on, asked for once. Fetching only the
   * active segment's would stall at every cut while the next one loaded.
   */
  const sourceAssetIds = Array.from(
    new Set(
      segments.flatMap((segment) => [
        ...segment.brief.sources.map((s) => s.assetId),
        // A composed segment names its footage inside the layout rather than in
        // sources, and it needs the same media to play (ADR 000066).
        ...assetsIn(segment.brief.layout),
      ]),
    ),
  );
  const sourceAssets = useQueries({
    queries: sourceAssetIds.map((assetId) => ({
      queryKey: ["asset", spaceId, assetId],
      queryFn: () => api.getAsset(spaceId, assetId),
      staleTime: 5 * 60 * 1000,
    })),
  });
  const mediaOf = new Map<string, string>();
  sourceAssetIds.forEach((assetId, i) => {
    const url = sourceAssets[i]?.data?.mediaUrl;
    if (url) mediaOf.set(assetId, url);
  });

  // The first source is the one played. A segment that draws on several is a
  // cut between them, and cutting is what a render does; this shows the
  // material rather than pretending to be the edit.
  const firstSource = active?.brief.sources[0];
  const rawFootage = firstSource ? mediaOf.get(firstSource.assetId) : undefined;

  const watchable = madePicture ?? rawFootage;

  // A composition is drawn from its layout rather than played from one file,
  // so several elements are running and none of them is the authority on where
  // the segment is (ADR 000066).
  const composing = !madePicture && Boolean(active?.brief.layout);

  /*
   * The segment's instructions to the renderer, for as long as this stands in
   * for it (ADR 000068).
   *
   * Every one of these branches is showing what the render will make out of:
   * `frameFilter` fits an uncut source or a still into the frame the way these
   * notes ask and pads what is left with their colour, and a segment with
   * nothing on it at all is a canvas of that colour for its own length
   * (apps/api/internal/render/frame.go, animatic.go). Drawing them all
   * letterboxed against black regardless showed a frame nobody was going to
   * make, and the person only found out by paying for the encode (issue #261).
   *
   * Empty once the picture exists. A rendition has its fit and its bars encoded
   * into it already, so resolving the notes over the top would draw them twice.
   */
  const framing = madePicture ? [] : (active?.brief.renderNotes ?? []);

  /*
   * The offset into whatever is playing. A rendition starts at zero, so the
   * offset is the distance into the segment. Raw footage does not: it starts
   * wherever the brief says, so the same distance is measured from there.
   */
  const intoSegment = active ? Math.max(0, at - active.startSec) : 0;
  const seekTo = madePicture ? intoSegment : (firstSource?.range.startSec ?? 0) + intoSegment;

  /*
   * The still to hold, on the renderer's own division (issue #268).
   *
   * A brief carries up to twelve, and the animatic gives each an equal slice of
   * the segment and cuts between them (ADR 000065). Showing the first for the
   * whole length made a shot planned as three frames look like a freeze: the
   * same duration, a different edit, and pacing is the one thing this player
   * exists to show.
   *
   * Asked of the API by its place in the brief: a still is a path in the store,
   * not somewhere the browser can fetch from (ADR 000070).
   */
  const stillIndex = active
    ? stillAt(intoSegment, active.endSec - active.startSec, active.brief.stills.length)
    : undefined;
  const still =
    active && stillIndex !== undefined
      ? api.segmentStillUrl(spaceId, projectId, active.id, stillIndex)
      : undefined;

  /**
   * Move to the next segment, or stop at the end of the chain.
   *
   * The playhead lands on the next segment's start rather than wherever the
   * last one ran to, so a picture that is a few frames longer than its segment
   * does not push everything after it out of step.
   */
  function advance(from: Segment) {
    const next = segments.find((s) => s.startSec >= from.endSec);
    if (!next) {
      setPlaying(false);
      onPlayhead(durationSec);
      return;
    }
    onPlayhead(next.startSec);
  }

  // Playing a segment that has a picture: the element runs and reports where it
  // is, and the chain's playhead follows it.
  useEffect(() => {
    const element = video.current;
    if (!element || !watchable || !active) return;

    const seek = () => {
      // Only when it is wrong. Assigning currentTime every render would fight
      // playback, since the element is the thing moving.
      if (Math.abs(element.currentTime - seekTo) > 0.35) {
        element.currentTime = seekTo;
      }
      if (playing) {
        const started = element.play() as Promise<void> | undefined;
        started?.catch(() => setPlaying(false));
      } else {
        // Pausing has to reach the element. This effect re-runs when playing
        // goes false and used to do nothing there, so the button changed its
        // own label and the video carried on.
        element.pause();
      }
    };

    if (element.readyState >= 1) seek();
    else element.addEventListener("loadedmetadata", seek, { once: true });
    return () => element.removeEventListener("loadedmetadata", seek);
    // intoSegment deliberately absent: this reacts to the segment changing and
    // to play being pressed, not to every frame of playback moving the chain.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchable, active?.id, playing]);

  useEffect(() => {
    const element = video.current;
    if (!element || !playing) return;
    if (!watchable || composing) return;

    const follow = () => {
      if (!active) return;
      // Back into chain time from whatever clock is running: a rendition's
      // starts at zero and raw footage's starts where the brief points.
      const from = madePicture ? 0 : (firstSource?.range.startSec ?? 0);

      // Nothing is reported until the element is where it was asked to be.
      //
      // A seek into a twenty minute press conference does not land the instant
      // it is assigned, and timeupdate fires meanwhile. Reading currentTime
      // then gives a chain time of minus a thousand seconds, no segment
      // matches it, and the player falls to the end of the chain: which looked
      // like it playing straight through and never advancing.
      if (element.currentTime < from - 0.5) return;

      const at = active.startSec + (element.currentTime - from);
      // Past the end of this segment: the next one takes over, which is what
      // makes this an assembly rather than a player pointed at one file.
      if (at >= active.endSec - 0.05) {
        advance(active);
        return;
      }
      onPlayhead(at);
    };

    element.addEventListener("timeupdate", follow);
    element.addEventListener("ended", follow);
    return () => {
      element.removeEventListener("timeupdate", follow);
      element.removeEventListener("ended", follow);
    };
  });

  // Playing a segment with no picture: nothing is running, so the clock is. Its
  // stills fill exactly as long as the segment does, cut between on the way
  // through, which is the pacing the storyboard is claiming.
  useEffect(() => {
    if (!playing || !active) return;
    // A composition has several elements and none of them is the authority on
    // where the segment is, so the clock is.
    if (watchable && !composing) return;

    const started = Date.now();
    const from = playheadSec;
    const timer = window.setInterval(() => {
      const at = from + (Date.now() - started) / 1000;
      if (at >= active.endSec - 0.05) {
        advance(active);
        return;
      }
      onPlayhead(at);
    }, 100);

    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, watchable, composing, active?.id]);

  if (segments.length === 0) return null;

  return (
    <div className="chain-player">
      {/*
       * The stage is the canvas, and the renderer paints it with the segment's
       * background whatever is drawn on top (ADR 000066, ADR 000068). A
       * composition paints its own as well, because LayoutStage is shown in
       * places that have no chain around them; the two are the same colour.
       */}
      <div className="chain-player-stage" style={stageStyle(framing)}>
        {/*
         * A composed segment is drawn from its layout rather than played from
         * one file, whether or not it has been rendered: the browser can
         * arrange the same description ffmpeg would (ADR 000066).
         */}
        {!madePicture && active?.brief.layout ? (
          <LayoutStage
            layout={active.brief.layout}
            segmentNotes={framing}
            mediaOf={mediaOf}
            intoSegmentSec={intoSegment}
            playing={playing}
          />
        ) : watchable ? (
          <video
            ref={video}
            key={active?.id}
            src={watchable}
            poster={poster}
            playsInline
            preload="metadata"
            style={frameStyle(framing)}
            aria-label="The chain"
          />
        ) : still ? (
          <img src={still} alt="" style={frameStyle(framing)} />
        ) : (
          <p className="muted">
            {active?.label ||
              active?.brief.direction ||
              active?.brief.narration ||
              "Nothing here yet"}
          </p>
        )}
      </div>

      <div className="chain-player-bar">
        <button
          type="button"
          onClick={() => {
            if (!playing && at >= durationSec) onPlayhead(0);
            setPlaying((was) => !was);
          }}
          aria-label={playing ? "Pause" : "Play the chain"}
        >
          {playing ? "Pause" : "Play"}
        </button>
        <span className="muted">
          {timecode(at)} of {timecode(durationSec)}
          {active && !madePicture && rawFootage && " · from the source, not cut yet"}
          {active && !watchable && " · not made yet"}
        </span>
      </div>
    </div>
  );
}
